import { Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { LabourTimeInputProps } from './LabourTimeInput.props';

const MAX_HOURS = 999;
const MAX_MINUTES = 59;

const clampInt = (raw: string, max: number): number | undefined => {
  if (raw === '') return undefined;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.min(parsed, max);
};

const parseValue = (value: string | null | undefined): { hours: string; minutes: string } => {
  if (!value) return { hours: '', minutes: '' };
  const hoursMatch = /(\d+)\s*h/i.exec(value);
  const minutesMatch = /(\d+)\s*m/i.exec(value);
  return {
    hours: hoursMatch ? hoursMatch[1] : '',
    minutes: minutesMatch ? minutesMatch[1] : '',
  };
};

const formatValue = (hours: string, minutes: string): string | undefined => {
  if (hours === '' && minutes === '') return undefined;
  const h = clampInt(hours, MAX_HOURS) ?? 0;
  const m = clampInt(minutes, MAX_MINUTES) ?? 0;
  return `${h}h ${m}m`;
};

export const LabourTimeInput: React.FC<LabourTimeInputProps> = ({
  value,
  onChange,
  title = 'Labour Time',
  error = false,
  showRequiredError = false,
  disabled = false,
  idPrefix = 'labour-time',
}) => {
  const [hours, setHours] = useState<string>(() => parseValue(value).hours);
  const [minutes, setMinutes] = useState<string>(() => parseValue(value).minutes);
  const lastEmittedRef = useRef<string | null | undefined>(value);

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    const next = parseValue(value);
    setHours(next.hours);
    setMinutes(next.minutes);
    lastEmittedRef.current = value;
  }, [value]);

  const emit = (nextHours: string, nextMinutes: string) => {
    const formatted = formatValue(nextHours, nextMinutes);
    lastEmittedRef.current = formatted;
    onChange(formatted);
  };

  const handleHoursChange = (raw: string) => {
    const next = raw === '' ? '' : String(clampInt(raw, MAX_HOURS) ?? 0);
    setHours(next);
    emit(next, minutes);
  };

  const handleMinutesChange = (raw: string) => {
    const next = raw === '' ? '' : String(clampInt(raw, MAX_MINUTES) ?? 0);
    setMinutes(next);
    emit(hours, next);
  };

  return (
    <Stack spacing={0.5}>
      {title && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {title}
        </Typography>
      )}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          id={`${idPrefix}-hours`}
          label="Hours"
          type="number"
          size="small"
          disabled={disabled}
          error={error}
          value={hours}
          slotProps={{
            htmlInput: { min: 0, max: MAX_HOURS, step: 1, 'aria-label': `${title} hours` },
          }}
          onChange={(event) => handleHoursChange(event.target.value)}
          sx={{ width: 120 }}
        />
        <TextField
          id={`${idPrefix}-minutes`}
          label="Minutes"
          type="number"
          size="small"
          disabled={disabled}
          error={error}
          value={minutes}
          slotProps={{
            htmlInput: { min: 0, max: MAX_MINUTES, step: 1, 'aria-label': `${title} minutes` },
          }}
          onChange={(event) => handleMinutesChange(event.target.value)}
          sx={{ width: 120 }}
        />
      </Stack>
      {error && showRequiredError && (
        <Typography variant="caption" sx={{ color: 'error.main' }}>
          *Required
        </Typography>
      )}
    </Stack>
  );
};
